import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Send, Mic, MicOff, Sparkles, FileText, BarChart2, Package, Users, Camera, Shield, Wifi, Star, RefreshCw, Volume2 } from 'lucide-react';
import { useCatalogStore } from '../store/catalogStore';
import { useWorkflowStore } from '../store/workflowStore';
import { useAdminStore } from '../store/adminStore';
import { buildSolution } from '../../ai/productRecommendation';
import { parseRequirement } from '../../ai/requirementParser';
import { detectLanguage } from '../../ai/languageDetector';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const suggestedPrompts = [
  { icon: Camera, text: 'Recommend CCTV for a 3000 sqft office', color: 'text-blue-500' },
  { icon: Shield, text: 'Best alarm system for a warehouse', color: 'text-[#7FA86A]' },
  { icon: Wifi, text: 'Starlink solution for rural hotel', color: 'text-green-500' },
  { icon: FileText, text: 'Generate a quotation for 8 cameras', color: 'text-purple-500' },
  { icon: BarChart2, text: 'Analyze this month\'s sales performance', color: 'text-indigo-500' },
  { icon: Package, text: 'Check low stock cameras', color: 'text-rose-500' },
  { icon: Users, text: 'Pending quotations list', color: 'text-teal-500' },
  { icon: Star, text: 'Top selling products', color: 'text-amber-500' },
];

const ADMIN_DEFAULT = `I'm your **K-Tec Admin AI Assistant**, connected to your live platform data. I can help you with:

• 📷 **CCTV & Camera Planning** — System design, product recommendations
• 📋 **Quotation Generation** — Create quotes using catalog products
• 📊 **Sales & Finance Analysis** — Revenue, profit, performance
• 📦 **Inventory** — Stock levels, reorder alerts
• 👥 **Customers & Orders** — CRM insights
• 🔧 **Warranty & Projects** — Status tracking

Ask me in English, Sinhala, or Singlish!`;

function MarkdownText({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-0.5">
      {lines.map((line, i) => {
        if (line.startsWith('## ') || line.startsWith('### '))
          return <h3 key={i} className="text-sm font-bold text-gray-900 mt-3 mb-1">{line.replace(/^#{2,3} /, '')}</h3>;
        if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**'))
          return <p key={i} className="text-sm font-semibold text-gray-800">{line.slice(2, -2)}</p>;
        if (line.startsWith('- ') || line.startsWith('• '))
          return <li key={i} className="text-sm text-gray-700 ml-3 list-disc">{line.replace(/^[-•] /, '')}</li>;
        if (line.startsWith('|'))
          return <p key={i} className="text-xs text-gray-600 font-mono overflow-x-auto">{line}</p>;
        if (line.trim() === '') return <br key={i} />;
        const boldified = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return <p key={i} className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: boldified }} />;
      })}
    </div>
  );
}

export default function AIAssistantPage() {
  const { catalogProducts, packages } = useCatalogStore();
  const { quotations, invoices, warranties } = useWorkflowStore();
  const { customers } = useAdminStore();

  const [messages, setMessages] = useState<Message[]>([
    { id: '1', role: 'assistant', content: ADMIN_DEFAULT, timestamp: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Admin AI Response Logic ───────────────────────────────────────────────
  function getAdminResponse(text: string): string {
    const lower = text.toLowerCase();

    // Sales / Finance queries
    if (/sales|revenue|income|profit|money|finance|profit.*month|month.*profit/i.test(lower)) {
      const paid = invoices.filter(i => i.paymentStatus === 'paid' || i.paymentStatus === 'partial');
      const totalRevenue = paid.reduce((s, i) => s + (i.paidAmount || i.grandTotal || 0), 0);
      const thisMonth = paid.filter(i => {
        const d = new Date(i.createdAt);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });
      const monthRevenue = thisMonth.reduce((s, i) => s + (i.paidAmount || i.grandTotal || 0), 0);
      return `## 📊 Sales Overview\n\n**This Month:** LKR ${monthRevenue.toLocaleString()} (${thisMonth.length} invoices)\n**Total All Time:** LKR ${totalRevenue.toLocaleString()} (${paid.length} invoices)\n\n_Data from ${invoices.length} total invoices in the system._\n\n⚠️ Live backend connection needed for detailed profit analysis.`;
    }

    // Stock / inventory queries
    if (/stock|inventory|camera.*stock|low.*stock|out.*stock/i.test(lower)) {
      const lowStock = catalogProducts.filter(p => p.inStock && p.stock <= p.minStockAlert);
      const outOfStock = catalogProducts.filter(p => !p.inStock || p.stock === 0);
      let resp = `## 📦 Stock Status\n\n**Low Stock Alerts (${lowStock.length} products):**\n`;
      if (lowStock.length > 0) {
        lowStock.slice(0, 8).forEach(p => {
          resp += `• ${p.name.substring(0, 40)} — **${p.stock} units** left\n`;
        });
      } else {
        resp += '✅ No low stock alerts\n';
      }
      resp += `\n**Out of Stock: ${outOfStock.length} products**\n`;
      resp += `\n**Total Products in Catalog: ${catalogProducts.length}**`;
      return resp;
    }

    // Specific brand stock
    const brandMatch = lower.match(/\b(dahua|hikvision|ezviz|imou)\b/i);
    if (brandMatch && /stock|camera|how many|keeya/i.test(lower)) {
      const brandName = brandMatch[1].toLowerCase();
      const brandId = brandName === 'dahua' ? 'b1' : brandName === 'hikvision' ? 'b2' :
        brandName === 'ezviz' ? 'b3' : brandName === 'imou' ? 'b4' : '';
      if (brandId) {
        const bProducts = catalogProducts.filter(p => p.brandId === brandId);
        const totalStock = bProducts.reduce((s, p) => s + (p.stock || 0), 0);
        let resp = `## ${brandMatch[1]} Stock\n\n**Total products: ${bProducts.length}**\n**Total stock: ${totalStock} units**\n\n`;
        bProducts.slice(0, 6).forEach(p => {
          resp += `• ${p.name.substring(0, 45)} — ${p.stock} units (${p.inStock ? '✅' : '❌ OUT'})\n`;
        });
        return resp;
      }
    }

    // Pending quotations
    if (/pending.*quotation|quotation.*pending|pending quotes/i.test(lower)) {
      const pending = quotations.filter(q => q.status === 'draft' || q.status === 'sent');
      let resp = `## 📋 Pending Quotations (${pending.length})\n\n`;
      if (pending.length === 0) {
        resp += 'No pending quotations.';
      } else {
        pending.slice(0, 8).forEach(q => {
          resp += `• **${q.referenceNumber}** — ${q.customerName || 'No customer'} — LKR ${(q.grandTotal || 0).toLocaleString()} — _${q.status}_\n`;
        });
      }
      return resp;
    }

    // Warranty queries
    if (/warranty|expire|expiring/i.test(lower)) {
      const now = new Date();
      const expiringSoon = warranties.filter(w => {
        if (!w.expiryDate) return false;
        const exp = new Date(w.expiryDate);
        const daysLeft = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        return daysLeft > 0 && daysLeft <= 90;
      });
      let resp = `## 🛡️ Warranty Status\n\n**Total Warranties: ${warranties.length}**\n**Expiring in 90 days: ${expiringSoon.length}**\n\n`;
      if (expiringSoon.length > 0) {
        expiringSoon.slice(0, 6).forEach(w => {
          resp += `• **${w.warrantyId}** — ${w.customerName} — Expires: ${new Date(w.expiryDate!).toLocaleDateString()}\n`;
        });
      }
      return resp;
    }

    // Customer count
    if (/customer|client|how many customer/i.test(lower)) {
      return `## 👥 Customers\n\n**Total Customers: ${customers.length}**\n\n_Use the Customers page for full CRM view._`;
    }

    // CCTV recommendation
    if (/cctv|camera|recommend/i.test(lower)) {
      const lang = detectLanguage(text);
      const req = parseRequirement(text, lang);
      if (req.cameraQuantity || req.propertyType) {
        const solution = buildSolution(catalogProducts, req);
        if (solution.cameras.length > 0) {
          let resp = `## 🎯 AI CCTV Recommendation\n\n`;
          resp += `**${solution.title}**\n\n`;
          solution.cameras.forEach(c => {
            resp += `📷 **${c.quantity}× ${c.product.name.substring(0, 50)}**\n`;
            resp += `   Price: LKR ${(c.product.salePrice || c.product.regularPrice).toLocaleString()} each\n\n`;
          });
          if (solution.recorder) {
            resp += `🖥️ **Recorder:** ${solution.recorder.product.name.substring(0, 50)}\n`;
          }
          if (solution.storage) {
            resp += `💾 **Storage:** ${solution.storage.product.name.substring(0, 40)}\n`;
          }
          resp += `\n**Estimated Total: LKR ${solution.totalEstimate.toLocaleString()}**\n`;
          resp += `_Installation: LKR ${solution.installationCost.toLocaleString()} | Labour: LKR ${solution.labourCost.toLocaleString()}_\n\n`;
          resp += `⚠️ _AI estimate — verify with site survey before quoting_`;
          return resp;
        }
      }
      return `## 📷 CCTV Design\n\nTo recommend a system, please specify:\n• **Property type** (home, shop, office, warehouse)\n• **Number of cameras** needed\n• **Budget range** (optional)\n• **Special requirements** (full colour, audio, AI detection)\n\nExample: "8 camera system for a 2-floor office, budget 300k"`;
    }

    // Quotation generation intent
    if (/generate.*quotation|create.*quotation|hadanna|quotation.*8|camera.*quotation/i.test(lower)) {
      return `## 📋 Create Quotation\n\nTo generate a quotation:\n\n1. Go to **Quotations** → **New Quotation**\n2. Select customer or add new\n3. Add products from catalog\n4. Apply discounts if needed\n5. Save & send to customer\n\nOr describe the requirement here and I'll build the line items:\n"8× Dahua 4MP cameras, 1× 8CH NVR, 1× 2TB HDD for office installation"`;
    }

    // Sinhala / Singlish response
    const lang = detectLanguage(text);
    if (lang === 'sinhala') {
      return `Admin panel eka Sinhala questions support karanawa.\n\nAdmin portal eke available data:\n• **Stock:** ${catalogProducts.length} products\n• **Quotations:** ${quotations.length} total\n• **Invoices:** ${invoices.length} total\n• **Warranties:** ${warranties.length} total\n\nKoyi data eka oney da?`;
    }

    return `## 🤖 K-Tec Admin AI\n\nI have access to:\n• **${catalogProducts.length}** catalog products\n• **${quotations.length}** quotations\n• **${invoices.length}** invoices\n• **${warranties.length}** warranties\n• **${customers.length}** customers\n\n**Try asking:**\n- "Low stock cameras"\n- "This month's revenue"\n- "Pending quotations"\n- "Warranty expiring soon"\n- "Recommend 8 camera CCTV for office"\n\n⚠️ _Live backend data requires Laravel API connection for real-time accuracy._`;
  }

  const sendMessage = async (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    await new Promise(r => setTimeout(r, 600 + Math.random() * 500));
    const response = getAdminResponse(text);
    setIsTyping(false);
    setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'assistant', content: response, timestamp: new Date() }]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const clearChat = () => {
    setMessages([{ id: '1', role: 'assistant', content: ADMIN_DEFAULT, timestamp: new Date() }]);
  };

  const speakMessage = (text: string) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const stripped = text.replace(/\*\*|#{1,3}|_/g, '').substring(0, 400);
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(stripped));
  };

  return (
    <div className="h-full flex flex-col bg-[#FAFAFA]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200/80">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-200">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-semibold text-gray-900">K-Tec Admin AI</h1>
              <span className="text-[10px] font-semibold bg-gradient-to-r from-violet-500 to-indigo-600 text-white px-2 py-0.5 rounded-full">LIVE DATA</span>
            </div>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block animate-pulse" />
              Connected to {catalogProducts.length} products · {invoices.length} invoices · {warranties.length} warranties
            </p>
          </div>
        </div>
        <button
          onClick={clearChat}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="w-3 h-3" /> New Chat
        </button>
      </div>

      {/* Suggested prompts */}
      {messages.length === 1 && (
        <div className="px-6 py-3 bg-white border-b border-gray-100">
          <p className="text-xs text-gray-400 font-medium mb-2">Quick prompts:</p>
          <div className="flex flex-wrap gap-2">
            {suggestedPrompts.map((prompt, i) => {
              const Icon = prompt.icon;
              return (
                <button
                  key={i}
                  onClick={() => sendMessage(prompt.text)}
                  className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 hover:bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 transition-colors"
                >
                  <Icon className={`w-3 h-3 ${prompt.color}`} />
                  {prompt.text}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <AnimatePresence initial={false}>
          {messages.map(msg => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-gradient-to-br from-[#7FA86A] to-[#96BE82] text-white rounded-br-sm'
                  : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
              }`}>
                {msg.role === 'assistant' ? (
                  <>
                    <MarkdownText text={msg.content} />
                    <button
                      onClick={() => speakMessage(msg.content)}
                      className="mt-2 text-gray-400 hover:text-violet-500 transition-colors"
                      title="Read aloud"
                    >
                      <Volume2 size={12} />
                    </button>
                  </>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
                <p className={`text-[10px] mt-1 ${msg.role === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                  {msg.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-[#7FA86A] to-[#96BE82] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Users className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </motion.div>
          ))}

          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-3 justify-start"
            >
              <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map(i => (
                    <motion.div
                      key={i}
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.1 }}
                      className="w-1.5 h-1.5 bg-violet-400 rounded-full"
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 bg-white border-t border-gray-200/80">
        <form onSubmit={handleSubmit} className="flex items-center gap-3">
          <div className="flex-1 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-violet-200 focus-within:border-violet-400 transition-all">
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask about stock, sales, quotations, CCTV design…"
              className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none"
            />
            <button
              type="button"
              onClick={() => setIsListening(!isListening)}
              className={`p-1 rounded-lg transition-colors ${isListening ? 'text-red-500 animate-pulse' : 'text-gray-400 hover:text-gray-600'}`}
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          </div>
          <button
            type="submit"
            disabled={!input.trim() || isTyping}
            className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-violet-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-violet-300 transition-all"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
        <p className="text-center text-[10px] text-gray-400 mt-2">
          Connected to K-Tec local data · AI estimates require admin verification
        </p>
      </div>
    </div>
  );
}
